import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { useTranslation } from "react-i18next";
import {
  Inbox, Sparkles, Users, Repeat, MessageSquare, Zap, ShieldCheck, ArrowRight,
  Check, X, Moon, Sun, Mail, BrainCircuit, Lock, ChevronDown,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { LeaOrb } from "../components/Lea";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { BillingToggle, PlanPrice } from "../components/BillingToggle";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Nav() {
  const { dark, toggle } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  return (
    <header className="fixed top-0 inset-x-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="h-8 w-8 rounded-lg bg-primary dark:bg-electric flex items-center justify-center">
            <Inbox className="h-4.5 w-4.5 text-white" size={18} />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">{t("common.appName")}</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <a href="#fonctionnalites" className="hover:text-foreground transition-colors" data-testid="nav-features">{t("landing.navFeatures")}</a>
          <a href="#securite" className="hover:text-foreground transition-colors" data-testid="nav-security">{t("landing.navSecurity")}</a>
          <a href="#tarifs" className="hover:text-foreground transition-colors" data-testid="nav-pricing">{t("landing.navPricing")}</a>
          <a href="#faq" className="hover:text-foreground transition-colors" data-testid="nav-faq">{t("landing.navFaq")}</a>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button onClick={toggle} data-testid="theme-toggle" className="p-2 rounded-full hover:bg-secondary transition-colors">
            {dark ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          {user ? (
            <Link to="/app" data-testid="nav-dashboard-btn"
              className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity">
              {t("landing.openApp")}
            </Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block text-sm font-medium hover:text-electric transition-colors" data-testid="nav-login">{t("landing.login")}</Link>
              <Link to="/register" data-testid="nav-cta"
                className="rounded-full bg-electric text-white px-5 py-2 text-sm font-semibold hover:bg-blue-700 transition-colors">
                {t("landing.cta")}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function DashboardMock() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    setTilt({
      x: ((e.clientY - r.top) / r.height - 0.5) * -7,
      y: ((e.clientX - r.left) / r.width - 0.5) * 9,
    });
  };
  const tagClasses = {
    Prospect: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
    Lead: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
    Prospecto: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
    Client: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
    Cliente: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
    Facture: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400",
    Invoice: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400",
    Factura: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400",
  };
  const prClasses = { red: "bg-red-500", orange: "bg-orange-500", zinc: "bg-zinc-300 dark:bg-zinc-600" };
  const menu = t("landing.mockMenu");
  const rows = t("landing.mockRows");
  return (
    <div style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={() => setTilt({ x: 0, y: 0 })}>
      <motion.div
        ref={ref}
        data-testid="hero-dashboard-mock"
        className="relative rounded-2xl border border-border/70 bg-card shadow-[0_40px_120px_-20px_rgba(10,37,64,0.35)] overflow-hidden"
        initial={{ opacity: 0, y: 60, rotateX: 12 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`, transition: "transform 0.18s ease-out" }}
      >
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-48 w-[130%] bg-electric/10 blur-3xl pointer-events-none" />
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border/60 bg-secondary/40">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-xs text-muted-foreground font-medium">app.elvarify.com — Smart Inbox</span>
        </div>
        <div className="grid grid-cols-12">
          <div className="col-span-3 border-r border-border/60 p-4 hidden sm:block">
            {menu.map((item, idx) => (
              <div key={item} className={`text-xs px-3 py-2 rounded-lg mb-1 ${idx === 1 ? "bg-electric/10 text-electric font-semibold" : "text-muted-foreground"}`}>{item}</div>
            ))}
            <div className="mt-6 flex items-center gap-2 px-1">
              <LeaOrb size={22} />
              <span className="text-[11px] text-muted-foreground">{t("landing.mockLea")}</span>
            </div>
          </div>
          <div className="col-span-12 sm:col-span-9 p-4 sm:p-5 space-y-2.5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-display font-bold">{t("landing.mockTitle")}</p>
              <span className="text-[11px] rounded-full bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400 px-2 py-0.5 font-semibold">{t("landing.mockUrgent")}</span>
            </div>
            {rows.map((r, i) => (
              <motion.div key={r.name}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + i * 0.15, duration: 0.5 }}
                className="flex items-center gap-3 rounded-xl border border-border/60 bg-background px-3.5 py-3">
                <span className={`h-2 w-2 rounded-full shrink-0 ${prClasses[r.pr]}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold truncate">{r.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{r.sum}</p>
                </div>
                <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${tagClasses[r.tag]}`}>{r.tag}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Hero() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <section className="relative pt-36 pb-24 px-6 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 h-[500px] w-[500px] rounded-full bg-electric/8 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-primary/8 blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto relative">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-xs font-medium text-muted-foreground mb-10">
          <Sparkles size={13} className="text-electric" />
          {t("landing.badge")}
        </motion.div>
        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tighter max-w-5xl" data-testid="hero-title">
          <MaskedLine delay={0.15}>{t("landing.heroL1")}</MaskedLine>
          <MaskedLine delay={0.3}>{t("landing.heroL2a")}<span className="font-serif-display italic font-normal text-electric">{t("landing.heroL2b")}</span></MaskedLine>
          <MaskedLine delay={0.45}>{t("landing.heroL3")}</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}
          className="mt-8 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-subtitle">
          {t("landing.heroSub")}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4">
          <button onClick={() => navigate("/register")} data-testid="hero-cta-primary"
            className="group rounded-full bg-electric text-white px-8 py-4 text-sm font-bold hover:bg-blue-700 transition-colors flex items-center gap-2">
            {t("landing.heroCta1")}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <a href="#fonctionnalites" data-testid="hero-cta-secondary"
            className="rounded-full border border-border bg-card px-8 py-4 text-sm font-semibold hover:border-electric/50 hover:text-electric transition-colors">
            {t("landing.heroCta2")}
          </a>
        </motion.div>
        <div className="mt-20">
          <DashboardMock />
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const { t } = useTranslation();
  const items = t("landing.marquee");
  const line = [...items, ...items];
  return (
    <div className="border-y border-border/70 bg-card/60 py-5 overflow-hidden" data-testid="marquee">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {line.map((t, i) => (
          <span key={i} className="mx-8 font-serif-display italic text-xl text-muted-foreground flex items-center gap-8">
            {t} <span className="text-electric not-italic text-xs">●</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const CHAPTERS = [];

function Manifesto() {
  const { t } = useTranslation();
  const chapters = t("landing.chapters");
  return (
    <section className="max-w-7xl mx-auto px-6 py-28">
      <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-16">{t("landing.manifestoKicker")}</p>
      <div className="space-y-20">
        {chapters.map((c) => (
          <motion.div key={c.n} {...fadeUp} className="grid md:grid-cols-12 gap-6 items-baseline">
            <span className="md:col-span-2 font-display font-black text-6xl text-electric/15 dark:text-electric/25">{c.n}</span>
            <h2 className="md:col-span-3 font-display font-bold text-3xl tracking-tight">{c.title}</h2>
            <p className="md:col-span-7 text-base md:text-lg text-muted-foreground leading-relaxed">{c.text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const FEATURE_ICONS = [BrainCircuit, Users, Sparkles, Repeat, MessageSquare, Zap, Mail];
const FEATURE_SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-4", "md:col-span-4", "md:col-span-4"];

function Features() {
  const { t } = useTranslation();
  const features = t("landing.features");
  return (
    <section id="fonctionnalites" className="max-w-7xl mx-auto px-6 py-24">
      <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-4">{t("landing.featuresKicker")}</motion.p>
      <motion.h2 {...fadeUp} className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-16 max-w-3xl">
        {t("landing.featuresTitle")}
      </motion.h2>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {features.map((f, i) => {
          const Icon = FEATURE_ICONS[i];
          return (
          <motion.div key={f.title} {...fadeUp} data-testid={`feature-${i}`}
            className={`${FEATURE_SPANS[i]} group rounded-2xl border border-border bg-card p-8 card-hover`}>
            <div className="h-11 w-11 rounded-xl bg-electric/10 flex items-center justify-center mb-6 group-hover:bg-electric/20 transition-colors">
              <Icon size={20} className="text-electric" />
            </div>
            <h3 className="font-display font-bold text-xl mb-3">{f.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
          </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function Security() {
  const { t } = useTranslation();
  const items = [
    { icon: Lock, ...t("landing.security")[0] },
    { icon: ShieldCheck, ...t("landing.security")[1] },
    { icon: Check, ...t("landing.security")[2] },
  ];
  return (
    <section id="securite" className="bg-primary text-white dark:bg-card dark:text-foreground py-24">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] text-white/60 dark:text-muted-foreground mb-4">{t("landing.securityKicker")}</motion.p>
        <motion.h2 {...fadeUp} className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-16 max-w-2xl">
          {t("landing.securityTitle")}
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-8">
          {items.map((s) => (
            <motion.div key={s.t} {...fadeUp} className="rounded-2xl border border-white/10 dark:border-border p-8">
              <s.icon size={22} className="text-electric mb-5" />
              <h3 className="font-display font-bold text-lg mb-3">{s.t}</h3>
              <p className="text-sm text-white/70 dark:text-muted-foreground leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PLANS = [];

const PLAN_LOOKUP = { start: "elvarify_start_monthly", pro: "elvarify_pro_monthly",
                      team: "elvarify_team_monthly" };

function Pricing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [busyPlan, setBusyPlan] = useState("");
  const [interval, setBillingInterval] = useState("month");
  useEffect(() => {
    // Le backend reste l'autorité : plan, intervalle et abonnement réels lus côté serveur
    if (user) api.get("/billing/subscription").then((r) => {
      setSub(r.data);
      if (r.data?.has_subscription && r.data?.interval) setBillingInterval(r.data.interval);
    }).catch(() => {});
  }, [user]);
  const plans = t("landing.plans");
  const custom = t("landing.customPlan");

  const choosePlan = async (p) => {
    // La périodicité voyage DANS le lookup key (…_monthly / …_yearly) : l'intention
    // prospect est ainsi conservée telle quelle jusqu'au checkout après authentification.
    const lookupKey = interval === "year" ? `elvarify_${p.key}_yearly` : PLAN_LOOKUP[p.key];
    if (!user) {
      sessionStorage.setItem("elvarify-intent-plan", lookupKey);
      navigate("/register");
      return;
    }
    const subInterval = sub?.interval || "month";
    const isCurrent = sub && sub.plan === p.key && (!sub.has_subscription || subInterval === interval);
    if (!sub || isCurrent || busyPlan) return;
    setBusyPlan(p.key);
    try {
      if (sub.has_subscription) {
        // Abonné : upgrade immédiat / changement de périodicité / downgrade programmé
        // sur l'abonnement EXISTANT — jamais de seconde souscription
        const { data } = await api.post("/billing/change-plan", { lookup_key: lookupKey });
        if (data.scheduled) toast.success(t("compte.planScheduled", { plan: p.name }));
        else if (data.changed) toast.success(t("compte.planChanged"));
        navigate("/app/compte");
      } else {
        // Gratuit connecté : Checkout Stripe pour SON workspace, sans reconnexion
        const { data } = await api.post("/payments/checkout", {
          lookup_key: lookupKey, origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(fmtErr(e));
      setBusyPlan("");
    }
  };

  return (
    <section id="tarifs" className="max-w-7xl mx-auto px-6 py-24">
      <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-4">{t("landing.pricingKicker")}</motion.p>
      <motion.h2 {...fadeUp} className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-4">{t("landing.pricingTitle")}</motion.h2>
      <motion.p {...fadeUp} className="text-muted-foreground mb-10">{t("landing.pricingSub")}</motion.p>
      <motion.div {...fadeUp} className="mb-12">
        <BillingToggle interval={interval} onChange={setBillingInterval} />
      </motion.div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
        {plans.map((p) => {
          const popular = p.key === "pro";
          const subInterval = sub?.interval || "month";
          const owns = !!(user && sub && sub.plan === p.key);
          const isCurrent = owns && (!sub.has_subscription || subInterval === interval);
          const ownsOtherInterval = owns && sub.has_subscription && subInterval !== interval;
          const ctaLabel = !user ? t("landing.tryPlan", { name: p.name })
            : isCurrent ? t("compte.current")
            : ownsOtherInterval ? (interval === "year" ? t("compte.switchToYearly") : t("compte.switchToMonthly"))
            : sub?.plan === "free" || !sub ? t("compte.choose", { name: p.name })
            : t("compte.upgradeTo", { name: p.name });
          return (
          <motion.div key={p.key} {...fadeUp} data-testid={`plan-${p.key}`}
            className={`relative rounded-2xl border p-8 card-hover flex flex-col ${popular ? "border-electric bg-card shadow-[0_20px_60px_-15px_rgba(0,71,255,0.25)]" : "border-border bg-card"}`}>
            {isCurrent ? (
              <span className="absolute -top-3 left-8 rounded-full bg-foreground text-background text-[11px] font-bold px-3 py-1" data-testid={`plan-${p.key}-current`}>{t("compte.current")}</span>
            ) : popular && (
              <span className="absolute -top-3 left-8 rounded-full bg-electric text-white text-[11px] font-bold px-3 py-1">{t("landing.popular")}</span>
            )}
            <h3 className="font-display font-bold text-lg">{p.name}</h3>
            {p.audience && <p className="text-xs text-muted-foreground mt-1 mb-4">{p.audience}</p>}
            <PlanPrice priceMonth={p.price} priceYear={p.priceYear} interval={interval} />
            <ul className="space-y-3 mb-10 flex-1">
              {p.features.map((f) => (
                <li key={f.t} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  {f.in
                    ? <Check size={15} className="text-success mt-0.5 shrink-0" />
                    : <X size={15} className="text-destructive/50 mt-0.5 shrink-0" />}
                  <span className={f.in ? "" : "text-muted-foreground/60"}>{f.t}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => choosePlan(p)} disabled={isCurrent || !!busyPlan} data-testid={`plan-${p.key}-cta`}
              className={`w-full rounded-full py-3 text-sm font-bold transition-colors mt-auto ${popular ? "bg-electric text-white hover:bg-blue-700" : "border border-border hover:border-electric/50 hover:text-electric"} disabled:opacity-60`}>
              {busyPlan === p.key ? t("compte.redirecting") : ctaLabel}
            </button>
          </motion.div>
          );
        })}
        {/* Offre SUR MESURE : aucun prix, aucun abonnement Stripe — simple contact commercial */}
        <motion.div {...fadeUp} data-testid="plan-custom"
          className="relative rounded-2xl border border-dashed border-border bg-secondary/30 p-8 card-hover flex flex-col">
          <h3 className="font-display font-bold text-lg">{custom.name}</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-4">{custom.audience}</p>
          <div className="mb-6 flex items-baseline">
            <span className="font-display text-2xl font-extrabold tracking-tight text-muted-foreground">{custom.pricingNote}</span>
          </div>
          <ul className="space-y-3 mb-10 flex-1">
            {custom.features.map((f) => (
              <li key={f.t} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                <Check size={15} className="text-success mt-0.5 shrink-0" /> {f.t}
              </li>
            ))}
          </ul>
          <button onClick={() => navigate("/contact")} data-testid="plan-custom-cta"
            className="w-full rounded-full py-3 text-sm font-bold border border-border hover:border-electric/50 hover:text-electric transition-colors mt-auto">
            {custom.cta}
          </button>
        </motion.div>
      </div>
      <p className="mt-8 text-center text-xs text-muted-foreground max-w-2xl mx-auto leading-relaxed" data-testid="pricing-legal-consent">
        {t("legal.consentPre")}
        <Link to="/conditions" className="text-electric hover:underline">{t("legal.docNames.conditions")}</Link>
        {t("legal.consentMid")}
        <Link to="/confidentialite" className="text-electric hover:underline">{t("legal.docNames.confidentialite")}</Link>
        {t("legal.consentEnd")}
      </p>
    </section>
  );
}

const FAQS = [];

function Faq() {
  const [open, setOpen] = useState(0);
  const { t } = useTranslation();
  const faqs = t("landing.faq");
  return (
    <section id="faq" className="max-w-4xl mx-auto px-6 py-24">
      <motion.h2 {...fadeUp} className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-16">{t("landing.faqTitle")}</motion.h2>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <motion.div key={i} {...fadeUp} className="rounded-2xl border border-border bg-card overflow-hidden">
            <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-${i}`}
              className="w-full flex items-center justify-between px-6 py-5 text-left">
              <span className="font-semibold text-sm">{f.q}</span>
              <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
            </button>
            <motion.div initial={false} animate={{ height: open === i ? "auto" : 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
              <p className="px-6 pb-6 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </motion.div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function FinalCta() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <section className="max-w-7xl mx-auto px-6 pb-28">
      <motion.div {...fadeUp}
        className="relative overflow-hidden rounded-3xl bg-primary dark:bg-card px-8 py-20 sm:px-16 text-center border border-border">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-64 w-[120%] bg-electric/20 blur-3xl" />
        <h2 className="relative font-display font-black text-4xl sm:text-6xl tracking-tighter text-white mb-6">
          {t("landing.finalTitle1")}<br /><span className="font-serif-display italic font-normal text-electric">{t("landing.finalTitle2")}</span>
        </h2>
        <p className="relative text-white/70 max-w-xl mx-auto mb-10 text-sm sm:text-base">
          {t("landing.finalSub")}
        </p>
        <button onClick={() => navigate("/register")} data-testid="final-cta"
          className="relative rounded-full bg-electric text-white px-10 py-4 text-sm font-bold hover:bg-blue-600 transition-colors">
          {t("landing.finalCta")}
        </button>
      </motion.div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border py-10">
      <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-primary dark:bg-electric flex items-center justify-center">
            <Inbox size={12} className="text-white" />
          </div>
          <span className="font-display font-bold text-sm text-foreground">{t("common.appName")}</span>
        </div>
        <p>{t("landing.footerCopy")}</p>
        <div className="flex gap-6">
          <a href="#securite" className="hover:text-foreground transition-colors">{t("landing.navSecurity")}</a>
          <a href="#tarifs" className="hover:text-foreground transition-colors">{t("landing.navPricing")}</a>
          <a href="#faq" className="hover:text-foreground transition-colors">{t("landing.navFaq")}</a>
          <Link to="/contact" className="hover:text-foreground transition-colors" data-testid="footer-contact">{t("landing.footerContact")}</Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-8 pt-6 border-t border-border flex flex-wrap justify-center items-center gap-x-2 gap-y-2 text-[11px] text-muted-foreground" data-testid="footer-legal">
        {[
          { to: "/mentions-legales", label: t("legal.footer.mentions"), id: "mentions" },
          { to: "/conditions", label: t("legal.footer.conditions"), id: "conditions" },
          { to: "/confidentialite", label: t("legal.footer.privacy"), id: "privacy" },
          { to: "/cookies", label: t("legal.footer.cookies"), id: "cookies" },
          { to: "/dpa", label: t("legal.footer.dpa"), id: "dpa" },
          { to: "/contact", label: t("landing.footerContact"), id: "contact" },
        ].map((l, i, arr) => (
          <span key={l.id} className="flex items-center gap-2">
            <Link to={l.to} data-testid={`footer-legal-${l.id}`} className="hover:text-foreground transition-colors">{l.label}</Link>
            {i < arr.length - 1 && <span className="text-border select-none">·</span>}
          </span>
        ))}
      </div>
    </footer>
  );
}

export default function Landing() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const onAnchor = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (a) {
        e.preventDefault();
        lenis.scrollTo(a.getAttribute("href"), { offset: -70 });
      }
    };
    document.addEventListener("click", onAnchor);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); document.removeEventListener("click", onAnchor); };
  }, []);

  return (
    <div className="bg-background text-foreground" data-testid="landing-page">
      <Nav />
      <Hero />
      <Marquee />
      <Manifesto />
      <Features />
      <Security />
      <Pricing />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}
